import { FC, useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { themeColor } from '../../layout/theme/color'
import { aiTokens, sparkleKeyframes } from './aiGeneratorTokens'
import { useWorkflowGenerator } from './useWorkflowGenerator'

const accentButtonSx = {
  'background': aiTokens.accent,
  'color': '#fff',
  'fontWeight': 600,
  '&:hover': { background: aiTokens.accentHover },
} as const

interface Props {
  open: boolean
  onClose: () => void
  initialPrompt?: string
  initialSummary?: string
  requestId?: string
}

const AiGenerateDialog: FC<Props> = ({
  open,
  onClose,
  initialPrompt,
  initialSummary,
  requestId,
}) => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const generator = useWorkflowGenerator()
  const { setRequestId, startFromApprovedRequest } = generator
  const [text, setText] = useState('')

  useEffect(() => {
    if (!open) return
    // Approved request with an existing summary → skip re-analyze (LLM might produce a different summary than the one approved).
    if (requestId && initialSummary) {
      startFromApprovedRequest(initialPrompt ?? '', initialSummary, requestId)
      setText('')
      return
    }
    if (initialPrompt) {
      setText(initialPrompt)
    }
    if (requestId) {
      setRequestId(requestId)
    }
  }, [
    open,
    initialPrompt,
    initialSummary,
    requestId,
    setRequestId,
    startFromApprovedRequest,
  ])

  const inFlight = generator.step === 'analyzing' || generator.step === 'building'

  const handleClose = () => {
    if (inFlight) return
    setText('')
    generator.reset()
    onClose()
  }

  const handleSubmitDescription = () => {
    if (!text.trim()) return
    void generator.submitDescription(text.trim())
    setText('')
  }

  const handleEdit = () => {
    // Restore the original prompt so the admin can refine instead of retyping (Edit and Retry paths).
    const originalPrompt = generator.messages.find((m) => m.role === 'user')?.content ?? ''
    setText(originalPrompt)
    generator.goToDescribe()
  }

  const handleOpenWorkflow = () => {
    if (!generator.workflowId) return
    handleClose()
    navigate(`/workflow/${generator.workflowId}/show`)
  }

  const isAnalyzeStep = generator.step === 'describing' || generator.step === 'clarifying'
  const submitDisabled = !text.trim()
  const canBuild = !!generator.summary

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pr: 6 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            background: aiTokens.accentSoft,
            color: aiTokens.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 20 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {translate('workflow.ai.title')}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: 'absolute', right: 10, top: 10, color: themeColor.slate60 }}
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Box sx={{ height: 2, background: aiTokens.gradient }} />

      <DialogContent sx={{ pt: 3, pb: 1, minHeight: 280 }}>
        {generator.step === 'describing' && (
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: themeColor.slate60, lineHeight: 1.55 }}>
              {translate('workflow.ai.intro')}
            </Typography>
            <TextField
              label={translate('workflow.ai.descriptionLabel')}
              placeholder={translate('workflow.ai.descriptionPlaceholder')}
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline
              minRows={5}
              maxRows={12}
              fullWidth
              autoFocus
            />
          </Stack>
        )}

        {generator.step === 'analyzing' && (
          <CenteredSpinner label={translate('workflow.ai.analyzing')} />
        )}

        {generator.step === 'clarifying' && (
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {translate('workflow.ai.needsMoreInfo')}
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 3,
                m: 0,
                li: { mb: 0.5 },
              }}
            >
              {generator.questions.map((q) => (
                <li key={q}>
                  <Typography variant="body2" sx={{ color: themeColor.slate60 }}>
                    {q}
                  </Typography>
                </li>
              ))}
            </Box>
            <TextField
              label={translate('workflow.ai.answerLabel')}
              placeholder={translate('workflow.ai.answerPlaceholder')}
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              autoFocus
            />
          </Stack>
        )}

        {generator.step === 'summary' && (
          <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {translate('workflow.ai.summaryTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: themeColor.slate60 }}>
              {translate('workflow.ai.summaryHint')}
            </Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: '10px',
                border: `1px solid ${aiTokens.accentBorder}`,
                background: aiTokens.accentSoft,
                whiteSpace: 'pre-wrap',
                fontSize: 14,
                lineHeight: 1.6,
                color: themeColor.slate80,
              }}
            >
              {generator.summary}
            </Box>
          </Stack>
        )}

        {generator.step === 'building' && (
          <CenteredSpinner label={translate('workflow.ai.building')} />
        )}

        {generator.step === 'done' && (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: aiTokens.accentSoft,
                color: aiTokens.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {translate('workflow.ai.successTitle')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: themeColor.slate60, textAlign: 'center', maxWidth: 360 }}
            >
              {translate('workflow.ai.successMessage')}
            </Typography>
          </Stack>
        )}

        {generator.step === 'error' && (
          <Stack spacing={2}>
            <Box
              sx={{
                p: 2,
                borderRadius: '10px',
                border: `1px solid ${aiTokens.errorBorder}`,
                background: aiTokens.errorBg,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <ErrorOutlineIcon sx={{ color: 'rgba(194,66,66,0.85)', mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {translate('workflow.ai.errorTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: themeColor.slate60, mt: 0.5 }}>
                  {translate('workflow.ai.errorBody')}
                </Typography>
                {generator.errorMessage && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      color: themeColor.slate50,
                      fontFamily: 'monospace',
                    }}
                  >
                    {generator.errorMessage}
                  </Typography>
                )}
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        {isAnalyzeStep && (
          <Button
            variant="contained"
            disabled={submitDisabled}
            onClick={handleSubmitDescription}
            sx={accentButtonSx}
          >
            {translate('workflow.ai.submit')}
          </Button>
        )}
        {generator.step === 'summary' && (
          <>
            <Button onClick={handleEdit}>{translate('workflow.ai.edit')}</Button>
            <Button
              variant="contained"
              disabled={!canBuild}
              onClick={() => void generator.build()}
              sx={accentButtonSx}
            >
              {translate('workflow.ai.build')}
            </Button>
          </>
        )}
        {generator.step === 'done' && (
          <Button variant="contained" onClick={handleOpenWorkflow} sx={accentButtonSx}>
            {translate('workflow.ai.openWorkflow')}
          </Button>
        )}
        {generator.step === 'error' && (
          <Button onClick={handleEdit}>{translate('workflow.ai.retry')}</Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

const CenteredSpinner: FC<{ label: string }> = ({ label }) => (
  <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
    <Box
      sx={{
        position: 'relative',
        width: 56,
        height: 56,
        ...sparkleKeyframes,
      }}
    >
      <CircularProgress size={56} thickness={3} sx={{ color: aiTokens.accent }} />
      <AutoAwesomeIcon
        sx={{
          position: 'absolute',
          inset: 0,
          margin: 'auto',
          fontSize: 24,
          color: aiTokens.accent,
          animation: 'sparkle 1.4s ease-in-out infinite',
        }}
      />
    </Box>
    <Typography variant="body2" sx={{ color: themeColor.slate60 }}>
      {label}
    </Typography>
  </Stack>
)

export default AiGenerateDialog
